import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { type ActiveCompany } from '@bbkAdminRedux/app/reducers';
import { selectApp, selectCompany } from '@bbkAdminRedux/app/selectors';
import { PLANS } from '@bbkAdminRedux/rtkq/checkout.plans';
import {
  useSelectCurrentApp,
  useSelectCurrentCompany,
} from '@bbkAdminRedux/rtkq/current-user.slice';
import { ProductKey } from '@bbkAdminRedux/rtkq/internal.products.slice';

export function useHasSubscriptionId() {
  const company = useSelector(selectCompany);

  return useMemo(() => !!company?.external_billing_id, [company]);
}

export function useHasSubscription() {
  const company = useSelector(selectCompany);
  const hasSubId = useHasSubscriptionId();

  return useMemo(() => {
    return hasSubId && !company.trial;
  }, [hasSubId, company]);
}

function useCommonPlans(company: ActiveCompany | undefined) {
  const plan = company?.plan;
  const checkPlan = (plans: PLANS[]): boolean => !plan || plans.includes(plan);

  const isTrial = () => company?.trial ?? false;

  const isStarterFreePlan = () => checkPlan([PLANS.STARTER_FREE]);
  const isStarterPremiumPlan = () => checkPlan([PLANS.STARTER_PREMIUM]);
  const isEssentialsPlan = () => checkPlan([PLANS.ESSENTIALS]);
  const isPerformancePlan = () => checkPlan([PLANS.PERFORMANCE]);
  const isEnterprisePlan = () => checkPlan([PLANS.ENTERPRISE]);

  function checkAccess({
    check,
    fallback,
  }: {
    check: boolean;
    fallback: boolean;
  }) {
    return plan ? check : fallback;
  }

  // Trial || Essentials
  // Only 3 pages allowed
  const pageCreationLimit = checkAccess({
    check: isEssentialsPlan(),
    fallback: false,
  });

  // Performance || Enterprise
  const canEditCustomCSS = checkAccess({
    check: isPerformancePlan() || isEnterprisePlan(),
    fallback: false,
  });

  return {
    plan,
    isTrial: isTrial(),
    isStarterFreePlan: isStarterFreePlan(),
    isStarterPremiumPlan: isStarterPremiumPlan(),
    isEssentialsPlan: isEssentialsPlan(),
    isPerformancePlan: isPerformancePlan(),
    isEnterprisePlan: isEnterprisePlan(),
    // Features
    pageCreationLimit,
    canEditCustomCSS,
  };
}

export function useSubscriptionPlans() {
  const company = useSelectCurrentCompany();
  const features = useCommonPlans(company);
  const trialExpireDate = company?.trial_end_date;

  return useMemo(
    () => ({
      ...features,
      trialExpireDate,
    }),
    [features, trialExpireDate]
  );
}

export function usePlans() {
  const company = useSelectCurrentCompany();
  const features = useCommonPlans(company);
  const trialExpireDate = company?.trial_end_date;

  return useMemo(
    () => ({
      ...features,
      trialExpireDate,
    }),
    [features, trialExpireDate]
  );
}

export const useGetProductLicense = () => {
  const app = useSelector(selectApp);
  if (!app?.application_licenses)
    return { agsLicense: false, deflectLicense: true, growthV2License: false };
  const agsLicense =
    app.application_licenses.find(
      (license) =>
        license.active && license.product_license.product_key === ProductKey.AGS
    )?.active ?? false;
  const deflectLicense =
    app.application_licenses.find(
      (license) =>
        license.active &&
        license.product_license.product_key === ProductKey.DEFLECT
    )?.active ?? false;
  const growthV2License =
    app.application_licenses.find(
      (license) =>
        license.active &&
        license.product_license.product_key === ProductKey.GROWTH_V2
    )?.active ?? false;
  if (!agsLicense && !deflectLicense && !growthV2License) {
    return { agsLicense: false, deflectLicense: true, growthV2License: false };
  }
  return { agsLicense, deflectLicense, growthV2License };
};

export const useIsMigratedToV2 = () => {
  const currentApp = useSelectCurrentApp();
  const { growthV2License } = useGetProductLicense();
  return growthV2License && Boolean(currentApp?.migrated_to_growth_v2_at);
};

// Non-react component checks
export const canAccessSalesforceIntegration = (company: ActiveCompany) =>
  company.plan
    ? [PLANS.ENTERPRISE, PLANS.PERFORMANCE].includes(company.plan)
    : false;
